import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, CalendarDays, Printer, Trash2, FileText, Calendar, Download, FileSpreadsheet,
} from "lucide-react";
import MonthlyPrintReport from "@/components/MonthlyPrintReport";

const MONTHS_RO = [
  "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie",
];

function formatRoDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("ro-RO", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function Reports() {
  const { authAxios, logout, username, isViewer } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [monthlyData, setMonthlyData] = useState(null);
  const [showPrintMonthly, setShowPrintMonthly] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authAxios.get(`/reports?year=${year}&month=${month}`);
      setReports(res.data);
    } catch (err) {
      toast.error("Eroare la încărcare rapoarte");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  const generateMonthly = async () => {
    try {
      const res = await authAxios.get(`/reports/monthly/${year}/${month}`);
      setMonthlyData(res.data);
      setShowPrintMonthly(true);
      if (res.data.days_count === 0) {
        toast.warning("Nu există rapoarte pentru luna selectată");
      } else {
        toast.success(`Raport lunar generat: ${res.data.days_count} zile`);
      }
    } catch (err) {
      toast.error("Eroare la generare raport lunar");
    }
  };

  const onDelete = async (id) => {
    if (!confirm("Ștergi raportul acestei zile?")) return;
    try {
      await authAxios.delete(`/reports/${id}`);
      setReports((prev) => prev.filter((r) => r.id !== id));
      toast.success("Raport șters");
    } catch (err) {
      toast.error("Eroare la ștergere");
    }
  };

  const downloadFile = async (url, filename) => {
    try {
      const res = await authAxios.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error("Eroare la descărcare");
    }
  };

  const onDownloadDaily = (date) => {
    downloadFile(`/reports/by-date/${date}/excel`, `raport_${date}.xlsx`);
  };

  const onDownloadMonthly = () => {
    downloadFile(`/reports/monthly/${year}/${month}/excel`, `raport_lunar_${year}_${String(month).padStart(2, "0")}.xlsx`);
  };

  const years = useMemo(() => {
    const arr = [];
    for (let y = now.getFullYear() - 2; y <= now.getFullYear() + 1; y++) arr.push(y);
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="back-to-dashboard"
              className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-amber-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Înapoi la producție
            </Link>
            <div className="h-5 w-px bg-neutral-200" />
            <h1 className="text-lg font-bold text-neutral-900">Rapoarte salvate</h1>
            <nav className="hidden md:flex items-center gap-1 ml-4 border-l border-neutral-200 pl-4">
              <Link to="/" data-testid="nav-dashboard" className="text-xs px-2 py-1 rounded hover:bg-neutral-100 text-neutral-700">Producție Zi</Link>
              <Link to="/inventar" data-testid="nav-inventar" className="text-xs px-2 py-1 rounded hover:bg-neutral-100 text-neutral-700">Inventar</Link>
              <Link to="/rapoarte" data-testid="nav-rapoarte" className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-800 font-semibold">Rapoarte</Link>
              <Link to="/calculator" data-testid="nav-calculator" className="text-xs px-2 py-1 rounded hover:bg-neutral-100 text-neutral-700">Calculator</Link>
            </nav>
          </div>
          <button
            data-testid="reports-logout"
            onClick={logout}
            className="text-xs text-neutral-500 hover:text-neutral-900"
          >
            {username} · Deconectare
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {!isViewer && (
          <div className="mb-4 flex items-center gap-3">
            <Link
              to="/inventar"
              data-testid="goto-inventory"
              className="inline-flex items-center gap-1.5 text-xs text-amber-700 hover:text-amber-800 font-semibold"
            >
              → Gestionează inventarul lunar
            </Link>
          </div>
        )}
        {isViewer && (
          <div className="mb-4 flex items-center gap-3">
            <Link
              to="/inventar"
              data-testid="goto-inventory"
              className="inline-flex items-center gap-1.5 text-xs text-amber-700 hover:text-amber-800 font-semibold"
            >
              → Vezi inventarul lunar
            </Link>
          </div>
        )}

        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-5 mb-6">
          <div className="flex flex-wrap items-end gap-5">
            <div>
              <label className="text-[10px] font-semibold tracking-wider uppercase text-neutral-500 mb-1 block">An</label>
              <select
                data-testid="year-select"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value, 10))}
                className="rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold tracking-wider uppercase text-neutral-500 mb-1 block">Lună</label>
              <select
                data-testid="month-select"
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                className="rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
              >
                {MONTHS_RO.map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div className="flex-1" />
            <button
              data-testid="download-monthly-excel"
              onClick={onDownloadMonthly}
              className="inline-flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 px-3 py-2 text-sm font-semibold text-emerald-800 transition-colors"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Excel lunar
            </button>
            <button
              data-testid="generate-monthly-button"
              onClick={generateMonthly}
              className="inline-flex items-center gap-2 rounded-md bg-amber-600 hover:bg-amber-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors"
            >
              <FileText className="w-4 h-4" />
              Generează raport lunar
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-neutral-200 bg-neutral-50/50">
            <h3 className="text-sm font-bold text-neutral-900">
              Rapoarte zilnice · {MONTHS_RO[month - 1]} {year}
            </h3>
            <p className="text-xs text-neutral-500 mt-0.5">
              {reports.length} {reports.length === 1 ? "raport salvat" : "rapoarte salvate"}
            </p>
          </div>
          {loading && <div className="p-8 text-center text-sm text-neutral-400">Se încarcă...</div>}
          {!loading && reports.length === 0 && (
            <div className="p-12 text-center">
              <div className="w-10 h-10 mx-auto rounded-full bg-neutral-100 flex items-center justify-center mb-3">
                <Calendar className="w-5 h-5 text-neutral-400" />
              </div>
              <p className="text-sm text-neutral-500">Niciun raport salvat pentru această perioadă.</p>
            </div>
          )}
          {!loading && reports.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-semibold tracking-wider uppercase text-neutral-500 bg-neutral-50">
                  <th className="px-5 py-2.5 w-12">#</th>
                  <th className="px-5 py-2.5">Data</th>
                  <th className="px-5 py-2.5 text-right w-32">Produse</th>
                  <th className="px-5 py-2.5 text-right w-32">Materii prime</th>
                  <th className="px-5 py-2.5 text-right w-32">Total consum (kg)</th>
                  <th className="px-5 py-2.5 w-24"></th>
                </tr>
              </thead>
              <tbody data-testid="reports-list">
                {reports.map((r, idx) => {
                  const totalConsumption = r.materials.reduce((s, m) => s + (m.consumption || 0), 0);
                  return (
                    <tr key={r.id} className="border-t border-neutral-100 hover:bg-amber-50/30 transition-colors">
                      <td className="px-5 py-2.5 text-neutral-400 text-xs tabular-nums">{idx + 1}</td>
                      <td className="px-5 py-2.5 font-medium text-neutral-900">
                        <Link
                          to={`/?date=${r.date}`}
                          data-testid={`report-link-${r.date}`}
                          className="text-amber-700 hover:text-amber-800 hover:underline"
                        >
                          {formatRoDate(r.date)}
                        </Link>
                        <span className="text-[10px] text-neutral-400 ml-2 tabular-nums">{r.date}</span>
                      </td>
                      <td className="px-5 py-2.5 text-right tabular-nums">{r.products.length}</td>
                      <td className="px-5 py-2.5 text-right tabular-nums">{r.materials.length}</td>
                      <td className="px-5 py-2.5 text-right tabular-nums font-semibold text-amber-700">
                        {totalConsumption.toFixed(3)}
                      </td>
                      <td className="px-5 py-2.5 text-right">
                        <button
                          data-testid={`download-report-${r.date}`}
                          onClick={() => onDownloadDaily(r.date)}
                          className="text-emerald-700 hover:text-emerald-800 p-1.5 rounded transition-colors mr-1"
                          title="Descarcă Excel"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        {!isViewer && (
                          <button
                            data-testid={`delete-report-${r.id}`}
                            onClick={() => onDelete(r.id)}
                            className="text-neutral-400 hover:text-red-600 p-1.5 rounded transition-colors"
                            title="Șterge"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showPrintMonthly && monthlyData && (
        <MonthlyPrintReport
          data={monthlyData}
          monthName={MONTHS_RO[month - 1]}
          onClose={() => setShowPrintMonthly(false)}
        />
      )}
    </div>
  );
}
